import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Business as ProjectIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonOff as PersonOffIcon,
} from "@mui/icons-material";
import { Project, UserSummary, statusOptions } from "./projectTypes";

interface ProjectListProps {
  projects: Project[];
  clientsById: Record<string, UserSummary>;
  loading: boolean;
  error: string | null;
  onCreate: () => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}

const formatDate = (value: string | null) => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not set";
  return parsed.toLocaleDateString();
};

const formatBudget = (value: number | null) => {
  if (!value && value !== 0) return "Not set";
  return `$${value.toLocaleString()}`;
};

export function ProjectList({
  projects,
  clientsById,
  loading,
  error,
  onCreate,
  onEdit,
  onDelete,
}: ProjectListProps) {
  const navigate = useNavigate();
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Projects
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
          Create Project
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Card>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Loading projects...</Typography>
          </CardContent>
        </Card>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent sx={{ py: 8, textAlign: "center" }}>
            <Box
              sx={{
                bgcolor: "grey.100",
                borderRadius: "50%",
                width: 80,
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <ProjectIcon sx={{ fontSize: 40, color: "text.secondary" }} />
            </Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              No projects yet
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
            >
              Get started by creating your first construction project. You'll be
              able to invite clients, track milestones, and send progress
              updates.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              sx={{ px: 4 }}
              onClick={onCreate}
            >
              Create Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => {
            const client = clientsById[project.client_id ?? ""];
            const clientClosed = !project.client_id;
            const clientDisplay =
              client?.name ||
              client?.email ||
              project.client_name_snap ||
              project.client_email_snap ||
              "Unknown";

            return (
              <Grid size={{ xs: 12, md: 6 }} key={project.id}>
                <Card
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                  onClick={() => navigate(`/contractor/projects/${project.id}`)}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {project.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {project.address || "No address"}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {clientClosed && (
                          <Tooltip title="Client account closed — info preserved for your records">
                            <Chip
                              icon={<PersonOffIcon />}
                              label="Account closed"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        <Chip
                          label={
                            statusOptions.find(
                              (option) => option.value === project.status,
                            )?.label ?? project.status
                          }
                          color={
                            project.status === "complete"
                              ? "success"
                              : project.status === "on_hold"
                                ? "warning"
                                : "primary"
                          }
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>

                    <Stack spacing={1} sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Client: {clientDisplay}
                        {clientClosed && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.disabled"
                            sx={{ ml: 0.5 }}
                          >
                            (account deleted)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start: {formatDate(project.start_date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Target: {formatDate(project.estimated_completion_date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Budget: {formatBudget(project.budget)}
                      </Typography>
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    <Tooltip title="Edit project">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(project);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete project">
                      <IconButton
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(project);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
